/**
 * Node Profile Editor Modal
 *
 * Form for creating/editing custom node profiles.
 */

import { useState, useEffect } from 'react';
import { saveCustomNodeProfile, type CustomNodeProfile } from '../../services/customProfileStorage';
import type { NodeProfile, NodeRole } from '../../types/additional-nodes';

interface NodeProfileEditorProps {
  profile: CustomNodeProfile | null;
  onClose: (saved: boolean) => void;
}

export function NodeProfileEditor({ profile, onClose }: NodeProfileEditorProps) {
  const isEdit = profile !== null;

  const [formData, setFormData] = useState<Partial<NodeProfile>>({
    id: profile?.id || '',
    name: profile?.name || '',
    role: profile?.role || 'cpu-compute',
    vendor: profile?.vendor || '',
    model: profile?.model || '',
    description: profile?.description || '',
    rackUnits: profile?.rackUnits || 2,
    powerKw: profile?.powerKw || 1.5,
    cpu: profile?.cpu || undefined,
    memoryGB: profile?.memoryGB || undefined,
    storageBays: profile?.storageBays || undefined,
    metadata: profile?.metadata || undefined,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (field: keyof NodeProfile, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCPUChange = (field: keyof NonNullable<NodeProfile['cpu']>, value: any) => {
    setFormData(prev => ({
      ...prev,
      cpu: { ...(prev.cpu || { sockets: 2, model: '', cores: 0 }), [field]: value },
    }));
  };

  const handleMetadataChange = (field: keyof NonNullable<NodeProfile['metadata']>, value: any) => {
    setFormData(prev => ({
      ...prev,
      metadata: { ...(prev.metadata || {}), [field]: value },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.id || !formData.name || !formData.vendor || !formData.model) {
      alert('Please fill in all required fields (ID, Name, Vendor, Model)');
      return;
    }

    // Generate ID from name if creating new
    if (!isEdit && !formData.id.trim()) {
      formData.id = formData.name.toLowerCase().replace(/\s+/g, '-');
    }

    try {
      saveCustomNodeProfile(formData as NodeProfile);
      onClose(true);
    } catch (error) {
      alert('Failed to save profile: ' + error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Edit Node Profile' : 'Create Node Profile'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => handleChange('id', e.target.value)}
                  disabled={isEdit}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="unique-id"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Unique identifier (cannot be changed after creation)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="Display name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="Dell, HPE, Supermicro, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="Model number/SKU"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value as NodeRole)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                >
                  <option value="cpu-compute">CPU Compute</option>
                  <option value="infrastructure">Infrastructure</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rack Units <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.rackUnits}
                  onChange={(e) => handleChange('rackUnits', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  max="10"
                  step="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Power (kW) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.powerKw}
                  onChange={(e) => handleChange('powerKw', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="0.1"
                  max="20"
                  step="0.1"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  rows={2}
                  placeholder="Brief description of the node"
                  required
                />
              </div>
            </div>
          </div>

          {/* Advanced Options */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              {showAdvanced ? '- Hide' : '+ Show'} Advanced Options
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4">
                {/* CPU Specifications */}
                <div className="border border-gray-200 rounded p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">CPU Specifications</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sockets</label>
                      <input
                        type="number"
                        value={formData.cpu?.sockets || ''}
                        onChange={(e) => handleCPUChange('sockets', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        min="1"
                        max="8"
                        placeholder="1, 2, 4..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cores</label>
                      <input
                        type="number"
                        value={formData.cpu?.cores || ''}
                        onChange={(e) => handleCPUChange('cores', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        min="1"
                        placeholder="64, 128..."
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPU Model</label>
                      <input
                        type="text"
                        value={formData.cpu?.model || ''}
                        onChange={(e) => handleCPUChange('model', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        placeholder="Xeon, EPYC..."
                      />
                    </div>
                  </div>
                </div>

                {/* Memory & Storage */}
                <div className="border border-gray-200 rounded p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Memory & Storage</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Memory (GB)</label>
                      <input
                        type="number"
                        value={formData.memoryGB || ''}
                        onChange={(e) => handleChange('memoryGB', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        min="1"
                        placeholder="512, 1024..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Storage Bays</label>
                      <input
                        type="number"
                        value={formData.storageBays || ''}
                        onChange={(e) => handleChange('storageBays', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        min="0"
                        placeholder="12, 24..."
                      />
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="border border-gray-200 rounded p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Metadata</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Product URL</label>
                      <input
                        type="url"
                        value={formData.metadata?.productUrl || ''}
                        onChange={(e) => handleMetadataChange('productUrl', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={formData.metadata?.notes || ''}
                        onChange={(e) => handleMetadataChange('notes', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        rows={2}
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded hover:bg-indigo-700"
            >
              {isEdit ? 'Save Changes' : 'Create Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
